/*
 * Copyright (c) Nexign, JSC, 2019-2022.
 */
/**
 * Footer component for BillsTable
 *
 * @author Evgenij Tyan
 * @author Anton Panasenko
 * @author Aleksandr Perkhunov
 * @author Yakov Gushchin
 * @version 1.20.1
 * @since 1.1.0
 */
import { FC, useCallback } from 'react';

import { connect } from 'react-redux';
import { createStructuredSelector } from 'reselect';

import {
    TableFooterButtonContainerStyled,
    TableFooterContainerStyled,
    TableFooterCounterContainerStyled,
    TableFooterLeftContainerStyled,
} from '../../../../../components/common/TableFooter';
import useTableFooterButtons, { IUseTableFooterButtons } from '../../../../../components/common/TableFooter/hooks/useTableFooterButtons';
import useTableFooterDisabled from '../../../../../components/common/TableFooter/hooks/useTableFooterDisabled';
import TableFooterItem from '../../../../../components/common/TableFooter/TableFooterItem';
import TableFooterShowMenu from '../../../../../components/common/TableFooter/TableFooterShowMenu';
import Basket from '../../../../../components/Table/components/footerModal/Basket/components/Basket';
import { ITableReducer } from '../../../../../components/Table/reducers/tableReducer';
import {
    makeSelectorTableBasketCount,
    makeSelectorTableButtons,
} from '../../../../../components/Table/selectors/tableSelectors';
import { TableNameType } from '../../../../../components/Table/types/tableTypes';
import {
    EGTMBasketSectionType,
    EGTMBasketValueType,
} from '../../../../../modules/analytics/GoogleTagManager/GTMOperations/GTMBasketOperations';
import { makeSelectTranslationPageBills } from '../../../../../modules/internationalization/intSelectors';
import { IPageBillsTranslation } from '../../../../../modules/internationalization/types';
import Guard from '../../../../../modules/role/Guard';
import { IRootReducer } from '../../../../../reducers/rootReducer';
import { MapPageBillsTypeToTableFooterName, PageBillsType } from '../../../pageBillsTypes';

export interface IBillsTableFooterProps {
    billsType: PageBillsType;
    clientId: string;
    isOpen?: boolean;
    onClick?: () => void;
}

export const BillsTableFooter: FC<IBillsTableFooterProps & IConnectedProps> = ({
                                                                                   billsType,
                                                                                   basketCount,
                                                                                   billsTranslation,
                                                                                   buttons,
                                                                                   isOpen,
                                                                                   onClick,
                                                                               }) => {

    const {
        billsCompare,
        reconciliation,
        wizardBillsDoc,
    } = useTableFooterButtons(MapPageBillsTypeToTableFooterName[billsType]);

    const {
        isBtnDisabled,
        getFooterButtonTooltipMsg,
    } = useTableFooterDisabled(MapPageBillsTypeToTableFooterName[billsType]);

    const getTooltipMsg = useCallback((item: IUseTableFooterButtons): string =>
        getFooterButtonTooltipMsg(item.id) || item.tooltipMsg, [getFooterButtonTooltipMsg]);

    const renderBtn = useCallback((item: IUseTableFooterButtons) => (
        <Guard accessKey={item.id} key={item.id}>
            <TableFooterItem
                {...item}
                isDisabled={isBtnDisabled(item.id)}
                tooltipMsg={getTooltipMsg(item)}
            />
        </Guard>
    ), [isBtnDisabled, getTooltipMsg]);

    return (
        <TableFooterContainerStyled className="table-footer bills-footer">
            <TableFooterShowMenu
                gtmSectionType={EGTMBasketSectionType.BILLS}
                isOpen={!!isOpen}
                onClick={onClick}
            />
            {
                !!basketCount &&
                <TableFooterCounterContainerStyled className="table-footer__counter">
                    <Basket
                        count={basketCount}
                        gtmProps={{
                            sectionType: EGTMBasketSectionType.BILLS,
                            valueType: EGTMBasketValueType.ACCOUNT,
                        }}
                        title={billsTranslation.tableFooter.basketTitle}
                    />
                </TableFooterCounterContainerStyled>
            }
            <TableFooterButtonContainerStyled>
                {
                    isOpen && buttons.length > 0 &&
                    <TableFooterLeftContainerStyled className="table-footer__left-container">
                        {[
                            { ...wizardBillsDoc, onClick: () => wizardBillsDoc.onClick(billsType) },
                            billsCompare,
                            reconciliation,
                        ].map(renderBtn)}
                    </TableFooterLeftContainerStyled>
                }
            </TableFooterButtonContainerStyled>
        </TableFooterContainerStyled>
    );
};

export interface IConnectedProps {
    basketCount: number | null;
    billsTranslation: IPageBillsTranslation;
    buttons: ITableReducer[TableNameType]['buttons'];
}

const mapStateToProps = createStructuredSelector<IRootReducer, IConnectedProps>({
    basketCount: makeSelectorTableBasketCount(TableNameType.bills),
    billsTranslation: makeSelectTranslationPageBills(),
    buttons: makeSelectorTableButtons(),
});

export default connect(mapStateToProps)(BillsTableFooter);
